import type { Translate } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import { MUTED } from '../shared';

const ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * When chats start, by weekday and hour: one hue, light to dark with the count (a sequential scale, never a rainbow),
 * with its scale under it. Each cell names its day, hour and count for a screen reader and on hover, and the busiest
 * cells carry their number, so the colour is never the only way to read it.
 */
export function HeatGrid({ grid, t, locale }: { grid: number[][]; t: Translate; locale: string }) {
  const max = Math.max(1, ...grid.flat());
  const day = (wd: number) => new Intl.DateTimeFormat(`${locale}-ZA`, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 25 + wd)));
  const top = [...grid.flat()].sort((a, b) => b - a)[2] ?? max;
  return (
    <div className="rr-insights__heat-wrap" data-testid="insights-heat">
      <table className="rr-insights__heat">
        <caption className="rr-visually-hidden">{t('insights.overview.heat')}</caption>
        <thead>
          <tr>
            <th scope="col"><span className="rr-visually-hidden">{t('insights.overview.heatDay')}</span></th>
            {Array.from({ length: 24 }, (_, h) => <th key={h} scope="col">{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</th>)}
          </tr>
        </thead>
        <tbody>
          {ORDER.map((wd) => (
            <tr key={wd}>
              <th scope="row">{day(wd)}</th>
              {grid[wd].map((n, h) => {
                const label = t('insights.overview.heatCell', { day: day(wd), hour: String(h).padStart(2, '0'), count: formatCount(n) });
                return (
                  <td key={h} title={label} aria-label={label}
                    style={{ background: n === 0 ? 'var(--rr-insights-heat-empty)' : `color-mix(in srgb, var(--rr-color-primary) ${Math.round(18 + (n / max) * 82)}%, var(--rr-insights-heat-empty))` }}
                    className={n >= top && n > 0 ? 'is-top' : undefined}>
                    {n >= top && n > 0 ? formatCount(n) : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="rr-insights__heat-scale" aria-hidden>
        <span style={{ color: MUTED }}>{t('insights.overview.heatFew')}</span>
        <span className="rr-insights__heat-ramp" />
        <span style={{ color: MUTED }}>{t('insights.overview.heatMany', { count: formatCount(max) })}</span>
      </div>
    </div>
  );
}
